import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { applyTenantBranding } from "@/lib/tenantHost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicForm() {
  const { tenantSlug, formSlug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/marketing/public/${tenantSlug}/${formSlug}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        // Apply tenant branding (converts hex/hsl-string to proper HSL var
        // triples so `bg-primary`/`text-primary` render correctly).
        applyTenantBranding(r.data.tenant);
      })
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Form not found." }));
  }, [tenantSlug, formSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/marketing/public/${tenantSlug}/${formSlug}/submit`,
        { values, source: new URLSearchParams(window.location.search).get("src") || "web" }
      );
      setSuccess(data.message || "Thanks!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (state.loading) return <FullScreen><div className="text-muted-foreground">Loading…</div></FullScreen>;
  if (state.error) return <FullScreen><div className="text-muted-foreground">{state.error}</div></FullScreen>;

  const { tenant, form } = state.data;

  if (success) {
    return (
      <FullScreen>
        <Toaster position="top-center" richColors />
        <div className="max-w-md text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl">{success}</h1>
          <p className="text-muted-foreground text-sm">— {tenant.name}</p>
        </div>
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-lg space-y-6" data-testid="public-form">
        <div className="text-center space-y-1">
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary">{tenant.name}</div>
          <h1 className="font-display text-3xl">{form.name}</h1>
          {tenant.tagline && <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{tenant.tagline}</p>}
        </div>
        {form.intro_text && <p className="text-sm text-muted-foreground text-center">{form.intro_text}</p>}
        <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-6">
          {form.fields.map((f) => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}{f.required && " *"}</Label>
              {f.type === "textarea" ? (
                <Textarea
                  rows={3}
                  required={f.required}
                  value={values[f.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  data-testid={`public-field-${f.key}`}
                />
              ) : f.type === "select" ? (
                <select
                  required={f.required}
                  value={values[f.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  data-testid={`public-field-${f.key}`}
                >
                  <option value="">Select…</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input
                  type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text"}
                  required={f.required}
                  value={values[f.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  data-testid={`public-field-${f.key}`}
                />
              )}
            </div>
          ))}
          <Button type="submit" disabled={submitting} className="w-full rounded-full" data-testid="public-form-submit">
            {submitting ? "Sending…" : "Submit"}
          </Button>
        </form>
        <div className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Powered by Kairosa
        </div>
      </div>
    </FullScreen>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      {children}
    </div>
  );
}
